/**
 * Fin de mandat — l'écran iconique qui pose la signature historique.
 *
 * Se déclenche quand `worldState.polity.mandate_ended === true` (le moteur
 * lève ce flag au tour qui atteint `max_turns`). Affiche :
 *   1. Un titre monumental « FIN DE MANDAT »
 *   2. La date finale (mois 60 / 5 ans)
 *   3. Une signature historique paramétrée par le profil politique :
 *      *« Vous êtes entré dans l'Histoire comme : <archétype> »*
 *   4. Trois lignes de bilan chiffré (cote, dette/PIB, satisfaction)
 *   5. Une phrase de clôture éditoriale
 *
 * Le bouton de fin permet de remettre à zéro la partie.
 *
 * Cf. project_retention_doctrine, levier #10 — sens narratif final.
 */

import { useGame } from "../store/useGame";
import { SUPPORT_URL } from "../support";
import { isVictory } from "../engine/moral-regime";
import { deriveAggregates } from "../engine/types";
import type {
  MandateEndCause,
  PoliticalProfile,
  WorldState,
} from "../engine/types";
import { renderShareImage, downloadDataUrl } from "../engine/share";
import {
  classifyArchetypeKey,
  ARCHETYPE_NAME,
  type ArchetypeKey,
} from "../engine/archetypes";
import {
  classifyCourant,
  COURANT_NAME,
  COURANT_OUTCOME_LABELS,
  type MandateOutcome,
} from "../engine/courant";

interface Signature {
  /* Courant politique du joueur (« Droite », « Écologiste »...). */
  courant: string;
  /* Label illustratif = courant x résultat (« Le Réformateur contesté »). */
  label: string;
  /* Ligne politique fine (archétype 4 axes — conservée en sous-titre). */
  archetype: string;
  closing: string;
}

/**
 * J1 — pré-titre + ton selon la cause de fin de mandat. Le terme normal
 * garde la signature politique ; la censure / faillite / coup d'État
 * écrasent l'archétype par une cause de chute plus brutale.
 */
function causeFraming(
  cause: MandateEndCause | null | undefined,
  turn: number,
): { pretitle: string; title: string; signaturePrefix: string } {
  switch (cause) {
    case "revolution":
      return {
        pretitle: `RÉVOLUTION · MOIS ${turn}`,
        title: "LE PEUPLE A REPRIS LA RUE",
        signaturePrefix:
          "Le régime que vous aviez verrouillé est tombé par la rue. Vous quittez l'Histoire comme :",
      };
    case "motion_censure":
      return {
        pretitle: `MOTION DE CENSURE · MOIS ${turn}`,
        title: "L'ASSEMBLÉE A TRANCHÉ",
        signaturePrefix:
          "Le Parlement a renversé votre gouvernement. Vous quittez l'Histoire comme :",
      };
    case "faillite":
      return {
        pretitle: `FAILLITE SOUVERAINE · MOIS ${turn}`,
        title: "LA FRANCE EN DÉFAUT",
        signaturePrefix:
          "Les marchés ont rendu leur verdict. Vous quittez l'Histoire comme :",
      };
    case "coup_etat":
      return {
        pretitle: `COUP D'ÉTAT · MOIS ${turn}`,
        title: "LA RUE A GAGNÉ",
        signaturePrefix:
          "L'institution a été emportée. Vous quittez l'Histoire comme :",
      };
    case "mise_sous_tutelle_ue":
      return {
        pretitle: `MISE SOUS TUTELLE EUROPÉENNE · MOIS ${turn}`,
        title: "BRUXELLES A TRANCHÉ",
        signaturePrefix:
          "L'Europe a repris la main sur l'Hexagone. Vous quittez l'Histoire comme :",
      };
    case "trahison_promesse":
      return {
        pretitle: `RÉVOLTE POPULAIRE · MOIS ${turn}`,
        title: "VOUS LUI AVIEZ PROMIS",
        signaturePrefix:
          "Le pays vous a chassé pour parjure — la promesse de campagne trahie. Vous quittez l'Histoire comme :",
      };
    case "term":
    default:
      return {
        pretitle: `FIN DE MANDAT · ${formatFinalDate(turn).toUpperCase()}`,
        title: "L'HEURE DU BILAN",
        signaturePrefix: "Vous êtes entré dans l'Histoire comme :",
      };
  }
}

/**
 * Lot 4 — cadrage « régime autoritaire ». Un président qui mène son mandat à
 * terme EN dictature (ou répression sévère) recevait jusqu'ici le bilan neutre
 * « L'heure du bilan », alors que le titre du navigateur affichait déjà « Moi
 * Dictateur » ([`moralRegime`]) — incohérence. On aligne l'écran de fin sur le
 * titre : tenir cinq ans par la force a son propre épilogue.
 */
function dictatorFraming(turn: number): {
  pretitle: string;
  title: string;
  signaturePrefix: string;
} {
  return {
    pretitle: `RÉGIME D'EXCEPTION · ${formatFinalDate(turn).toUpperCase()}`,
    title: "MOI DICTATEUR",
    signaturePrefix:
      "Vous avez tenu jusqu'au bout — mais par la peur. L'Histoire vous retiendra comme :",
  };
}

/**
 * VICTOIRE (2026-07-23) — mandat mené à terme + promesse(s) de campagne
 * tenue(s). La seule fin franchement triomphale : on a survécu ET tenu
 * parole. Prime sur le bilan neutre « L'heure du bilan ».
 */
function victoryFraming(turn: number): {
  pretitle: string;
  title: string;
  signaturePrefix: string;
} {
  return {
    pretitle: `VICTOIRE · ${formatFinalDate(turn).toUpperCase()}`,
    title: "PROMESSE TENUE",
    signaturePrefix:
      "Vous avez tenu cinq ans ET tenu parole. L'Histoire vous retiendra comme :",
  };
}

/**
 * R4 — Table des closings T60 indexée par ArchetypeKey. Chaque entrée
 * fournit 3 variantes de phrase de clôture (gloire / contesté / raté)
 * sélectionnées via `closingByBalance` selon la satisfaction et la
 * dette finales.
 */
const SIGNATURE_CLOSINGS: Record<
  ArchetypeKey,
  { glory: string; contested: string; failed: string }
> = {
  centriste_assume: {
    glory:
      "Vous laissez un pays à peu près tenu. L'Histoire pardonne ce genre de mandat.",
    contested:
      "Vous n'avez pas ouvert d'horizon, mais vous n'avez pas non plus laissé de cratère.",
    failed:
      "L'absence de cap a coûté — votre mandat sera décrit comme une parenthèse molle.",
  },
  neoconservateur: {
    glory:
      "Vos partisans parlent déjà de « doctrine » — vos opposants, de tournant historique.",
    contested: "Vous laissez un pays plus dur, et un débat tranché.",
    failed:
      "Le pays a tenu, mais à quel prix — une décennie sera nécessaire pour cicatriser.",
  },
  conservateur_liberal: {
    glory: "Vous laissez un pays cohérent à défaut d'avoir été aimable.",
    contested:
      "Les héritiers vous remercient, les enfants vous reprochent — c'était le pari.",
    failed:
      "L'austérité a creusé les fractures — la suite vous le pardonnera difficilement.",
  },
  progressiste_autoritaire: {
    glory:
      "Les réformes ont passé en force — l'Histoire dira si la force a payé.",
    contested: "Vous n'avez convaincu qu'à moitié, mais vous avez agi.",
    failed:
      "La gauche radicale vous reproche la méthode, le reste vous reproche les résultats.",
  },
  souverainiste_gauche: {
    glory:
      "Une voie inédite — protectionnisme et redistribution mêlés. Le modèle se discute déjà.",
    contested:
      "Vous avez brouillé la carte politique. Personne ne sait encore où vous classer.",
    failed:
      "L'expérience a échoué — Bruxelles vous garde rancune, votre base est partagée.",
  },
  national_populiste: {
    glory:
      "La nation d'abord, jusqu'au bout — vos électeurs parlent de vous comme d'un rempart. L'Europe, comme d'un précédent.",
    contested:
      "Moitié du pays vous voit en protecteur, l'autre en fossoyeur — vous avez gouverné sur une ligne de fracture.",
    failed:
      "L'isolement a coûté plus cher que promis — les frontières fermes n'ont pas retenu les capitaux, ni les colères.",
  },
  ecologiste_pragmatique: {
    glory: "Vous laissez un pays qui regarde enfin son siècle en face.",
    contested:
      "Vos écologistes vous trouvent timide, les autres trop ambitieux. Vous étiez au milieu.",
    failed:
      "Les promesses climat n'ont pas tenu — la déception est lourde sur votre génération.",
  },
  sociale_democrate: {
    glory: "Mitterrand-Delors revisité. Le modèle revient.",
    contested: "Vous laissez un pays apaisé sur le fond, fébrile sur la forme.",
    failed:
      "Le compromis n'a pas tenu — votre gauche vous lâche, votre droite triomphe.",
  },
  populiste_gouvernement: {
    glory: "Vous avez tenu un cap qu'aucun parti n'avait osé porter au bout.",
    contested:
      "Le pays vous adore ou vous déteste — l'entre-deux n'existe plus.",
    failed:
      "L'expérience populiste a heurté les institutions — les corps intermédiaires ne s'en remettent pas.",
  },
  libertaire_droite: {
    glory:
      "Marché libre, mœurs libres — l'Hexagone de la Silicon Sentier vous est reconnaissant.",
    contested:
      "Les retraités vous trouvent étranger, les startups vous adorent. C'est un choix.",
    failed:
      "L'utopie libertaire a heurté la réalité hexagonale — et la réalité a gagné.",
  },
  liberal_classique: {
    glory:
      "Compétitivité, modernisation, ouverture — la macronie première manière reconduite.",
    contested:
      "Vous avez réformé sans convaincre les territoires — les périphéries se souviendront.",
    failed:
      "Le libéralisme assumé n'a pas tenu socialement — le ressentiment vous suivra.",
  },
  etatiste_reformateur: {
    glory: "Pompidou aurait apprécié. C'est rare comme compliment.",
    contested: "L'État puissant a fonctionné — partiellement.",
    failed:
      "L'État puissant n'a pas suffi à tenir le pays. Le diagnostic d'échec sera dur.",
  },
  tenant_ordre: {
    glory: "L'ordre républicain est sauf — c'était votre seul mandat.",
    contested: "La police vous voue un culte discret. Les magistrats moins.",
    failed: "L'ordre s'est durci sans gagner en stabilité — l'équation perdue.",
  },
  souverainiste: {
    glory:
      "Vous avez rendu à l'Hexagone ce qu'il avait confié — une partie au moins.",
    contested: "Bruxelles vous a craint plus qu'aimé — c'était le pari.",
    failed: "La souveraineté retrouvée n'a pas compensé l'isolement subi.",
  },
  profil_composite: {
    glory:
      "Vous laissez un pays à peu près tenu. L'Histoire pardonne ce genre de mandat.",
    contested:
      "Vous n'avez pas ouvert d'horizon, mais vous n'avez pas non plus laissé de cratère.",
    failed:
      "L'absence de cap a coûté — votre mandat sera décrit comme une parenthèse molle.",
  },
};

/**
 * R4 — Identifie l'archétype politique final + sa phrase de clôture
 * conditionnée par satisfaction et dette/PIB. Délègue la classification
 * à `classifyArchetypeKey` (partagée avec ProfileCrystalOverlay).
 */
function classifySignature(
  p: PoliticalProfile,
  w: WorldState,
  victory: boolean,
): Signature {
  const key = classifyArchetypeKey(p);
  const closings = SIGNATURE_CLOSINGS[key];
  const agg = deriveAggregates(w);
  const sat = agg.meanSatisfaction;
  // Palier de sortie — partagé entre la phrase de clôture (par archétype)
  // et le label illustratif (par courant). 2026-07-23 : la GLOIRE est
  // désormais la victoire (promesse tenue), pas un seuil de satisfaction
  // hors d'atteinte. Sinon : contesté (pays tenu) ou raté.
  const outcome: MandateOutcome = victory
    ? "glory"
    : sat >= 35
      ? "contested"
      : "failed";
  const courantKey = classifyCourant(p);
  return {
    courant: COURANT_NAME[courantKey],
    label: COURANT_OUTCOME_LABELS[courantKey][outcome],
    archetype: ARCHETYPE_NAME[key],
    closing: closings[outcome],
  };
}

/**
 * N4 — Sparkline minimaliste de l'approbation. Courbe SVG de la
 * trajectoire ; 1 polyline lissée, 0 axe (lecture émotionnelle, pas
 * scientifique). Bornes 0..100, hauteur fixe.
 */
function ApprovalSpark({ values }: { values: number[] }) {
  if (values.length < 2) return null;
  const W = 600;
  const H = 64;
  const pad = 6;
  const innerW = W - pad * 2;
  const innerH = H - pad * 2;
  const points = values
    .map((v, i) => {
      const x = pad + (i / (values.length - 1)) * innerW;
      const y = pad + (1 - Math.max(0, Math.min(100, v)) / 100) * innerH;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <svg
      className="approval-spark"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      aria-label="Courbe d'approbation au fil du mandat"
    >
      {/* Ligne 50% (seuil de tenabilité) */}
      <line
        x1={pad}
        x2={W - pad}
        y1={pad + innerH / 2}
        y2={pad + innerH / 2}
        stroke="currentColor"
        strokeOpacity="0.15"
        strokeDasharray="4 4"
      />
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * L5b — verdict moral 4 axes (démocratie / économie / social / souveraineté)
 * dérivé de l'état final. Distinct du profil politique 4-axes (qui place
 * idéologiquement). Ici on juge l'*état du pays laissé*, pas la *posture*
 * du président. Donne au joueur une lecture immédiate de ce qu'il laisse.
 */
type MoralTone = "sauve" | "tenu" | "abime" | "ravage";

interface MoralAxis {
  label: string;
  verdict: string;
  tone: MoralTone;
  /** Lot 4 — les chiffres bruts derrière le verdict (rend le jugement lisible). */
  driver: string;
}

function toneFromScore(score: number): MoralTone {
  // score sur 100. 70+ sauvé, 50+ tenu, 30+ abîmé, sinon ravagé.
  if (score >= 70) return "sauve";
  if (score >= 50) return "tenu";
  if (score >= 30) return "abime";
  return "ravage";
}

function VERDICT_TEXT(tone: MoralTone): string {
  switch (tone) {
    case "sauve":
      return "préservé";
    case "tenu":
      return "tenu";
    case "abime":
      return "abîmé";
    case "ravage":
      return "ravagé";
  }
}

function moralVerdict(w: WorldState): MoralAxis[] {
  // Démocratie — bonne quand pas en état d'exception, répression basse.
  const repression = w.polity.repression_index ?? 0;
  const isDemocracy = w.polity.regime === "democracy";
  const democracyScore = isDemocracy
    ? Math.max(0, 100 - repression * 130)
    : Math.max(0, 30 - repression * 30);

  // Économie — pondère dette/PIB, balance budgétaire, currency.
  const debtRatio = (w.economy.public_debt / Math.max(w.economy.gdp, 1)) * 100;
  const debtScore = Math.max(0, 100 - Math.max(0, debtRatio - 80) * 1.2);
  const currency = w.economy.currency_strength;
  const economyScore = debtScore * 0.55 + currency * 0.45;

  // Social — satisfaction moyenne + écart inter-cohorte (proxy d'inégalité
  // perçue : si une CSP s'effondre, le bilan social est entaché même si
  // la moyenne tient).
  const cohorts = w.population.cohorts ?? [];
  const sats = cohorts.map((c) => c.satisfaction ?? 50);
  const minSat = sats.length ? Math.min(...sats) : 50;
  const meanSat = deriveAggregates(w).meanSatisfaction;
  const socialScore = meanSat * 0.7 + minSat * 0.3;

  // Souveraineté — dépendance étrangère faible + soft power élevé.
  let totalShare = 0;
  let count = 0;
  for (const c of w.geopolitics.countries) {
    for (const d of c.dependencies ?? []) {
      totalShare += d.share;
      count += 1;
    }
  }
  const depPct = count === 0 ? 0 : (totalShare / count) * 100;
  const sovereigntyScore = Math.max(0, 100 - depPct);

  return [
    {
      label: "Démocratie",
      tone: toneFromScore(democracyScore),
      verdict: democracyVerdictPhrase(democracyScore, isDemocracy),
      driver: `${isDemocracy ? "Régime démocratique" : "Régime autoritaire"} · répression ${Math.round(repression * 100)} %`,
    },
    {
      label: "Économie",
      tone: toneFromScore(economyScore),
      verdict: economyVerdictPhrase(economyScore, debtRatio),
      driver: `Dette ${debtRatio.toFixed(0)} % du PIB · monnaie ${Math.round(currency)}/100`,
    },
    {
      label: "Social",
      tone: toneFromScore(socialScore),
      verdict: socialVerdictPhrase(meanSat, minSat),
      driver: `Satisfaction moyenne ${Math.round(meanSat)} · cohorte la plus basse ${Math.round(minSat)}`,
    },
    {
      label: "Souveraineté",
      tone: toneFromScore(sovereigntyScore),
      verdict: sovereigntyVerdictPhrase(depPct),
      driver: `Dépendance étrangère ${depPct.toFixed(0)} %`,
    },
  ];
}

function democracyVerdictPhrase(score: number, isDemocracy: boolean): string {
  if (!isDemocracy) return "État d'exception prolongé, institutions affaiblies";
  if (score >= 70) return "Institutions préservées, libertés tenues";
  if (score >= 50)
    return "Régime debout, mais la répression a laissé des traces";
  if (score >= 30) return "Le contrat démocratique s'effrite";
  return "Démocratie abîmée — la prochaine équipe héritera d'un climat lourd";
}

function economyVerdictPhrase(score: number, debtRatio: number): string {
  if (score >= 70) return "Pays financièrement solide à l'arrivée";
  if (score >= 50)
    return `Dette à ${debtRatio.toFixed(0)} % du PIB, encore gouvernable`;
  if (score >= 30)
    return `Dette à ${debtRatio.toFixed(0)} % du PIB, marges étroites`;
  return `Faillite latente — dette à ${debtRatio.toFixed(0)} %, monnaie sous pression`;
}

function socialVerdictPhrase(meanSat: number, minSat: number): string {
  if (meanSat >= 55 && minSat >= 40) return "Pays globalement apaisé";
  if (meanSat >= 45) return "Pays tenu, mais une cohorte décroche";
  if (minSat < 25) return "Une partie du pays a décroché de la République";
  return "Tensions sociales installées, à durer";
}

function sovereigntyVerdictPhrase(depPct: number): string {
  if (depPct <= 15) return "Pays largement autonome";
  if (depPct <= 30) return `Dépendances tenables (${depPct.toFixed(0)} %)`;
  if (depPct <= 50)
    return `${depPct.toFixed(0)} % d'exposition aux fournisseurs étrangers`;
  return `Pays sous influence — ${depPct.toFixed(0)} % d'exposition critique`;
}

/**
 * L5b — phrase de presse fictive auto-générée. Donne au joueur le « titre
 * de demain matin » : Le Monde / Libération / Le Figaro / Mediapart selon
 * le profil politique. La date est le mois suivant la fin de mandat.
 */
function pressVerdict(w: WorldState): {
  outlet: string;
  date: string;
  headline: string;
} {
  const profile = w.polity.profile;
  // Outlet ≈ média qui se sentirait le plus concerné par le bilan.
  const outlet = pickOutlet(profile);
  const date = formatFinalDate(w.turn + 1);
  const headline = composePressHeadline(w);
  return { outlet, date, headline };
}

function pickOutlet(p: PoliticalProfile): string {
  if (p.economic < -0.2 && p.societal < -0.1) return "Libération";
  if (p.economic > 0.2 && p.authority > 0.1) return "Le Figaro";
  if (p.authority < -0.2) return "Mediapart";
  if (p.societal > 0.2) return "Valeurs actuelles";
  return "Le Monde";
}

function composePressHeadline(w: WorldState): string {
  const verdicts = moralVerdict(w);
  const traits: string[] = [];
  for (const a of verdicts) {
    if (a.tone === "sauve") traits.push(`${a.label.toLowerCase()} préservée`);
    else if (a.tone === "ravage")
      traits.push(`${a.label.toLowerCase()} sinistrée`);
  }
  if (traits.length === 0) {
    const sat = deriveAggregates(w).meanSatisfaction;
    if (sat >= 50)
      return "Un mandat plus tranquille qu'attendu, un héritage discret.";
    return "Cinq années à demi-mot, un pays à recoudre.";
  }
  if (traits.length === 1) {
    return `Le mandat aura été marqué par une ${traits[0]}.`;
  }
  return `Le mandat laisse un Hexagone à ${traits[0]} et ${traits[1]}.`;
}

function formatFinalDate(turn: number): string {
  const months = [
    "janvier",
    "février",
    "mars",
    "avril",
    "mai",
    "juin",
    "juillet",
    "août",
    "septembre",
    "octobre",
    "novembre",
    "décembre",
  ];
  const totalMonths = turn;
  const m = totalMonths % 12;
  const y = 2028 + Math.floor(totalMonths / 12);
  return `${months[m]} ${y}`;
}

export function MandateEndOverlay() {
  const worldState = useGame((s) => s.worldState);
  const pledges = useGame((s) => s.pledges);
  const indicatorHistory = useGame((s) => s.indicatorHistory);
  const resetGame = useGame((s) => s.resetGame);
  const reelectGame = useGame((s) => s.reelectGame);
  const openFeedback = useGame((s) => s.openFeedback);

  if (!worldState || !worldState.polity.mandate_ended) {
    return null;
  }

  const profile = worldState.polity.profile;
  // Victoire = mandat mené à terme + promesse(s) tenue(s).
  const victory = isVictory(worldState, pledges);
  const sig = classifySignature(profile, worldState, victory);
  const aggregates = deriveAggregates(worldState);
  const debt =
    (worldState.economy.public_debt / Math.max(worldState.economy.gdp, 1)) *
    100;
  const revenues = Object.values(worldState.economy.budget.revenues).reduce(
    (a, b) => a + b,
    0,
  );
  const expenses = Object.values(worldState.economy.budget.expenses).reduce(
    (a, b) => a + b,
    0,
  );
  const balance = (revenues - expenses) / 1e9;

  const handleRestart = () => {
    if (
      window.confirm(
        "Effacer la sauvegarde et recommencer une nouvelle partie ?",
      )
    ) {
      resetGame();
    }
  };

  const handleReelect = () => {
    if (
      window.confirm(
        "Vous repartez pour un mandat dans le même contexte historique. Continuer ?",
      )
    ) {
      reelectGame();
    }
  };

  const cause = worldState.polity.mandate_end_cause ?? "term";
  // Lot 4 — un mandat mené à terme EN régime autoritaire reçoit l'épilogue
  // « Moi Dictateur » (cohérent avec le titre du navigateur), pas le bilan neutre.
  const isAuthoritarian =
    worldState.polity.regime !== "democracy" ||
    (worldState.polity.repression_index ?? 0) >= 0.5;
  const dictatorEnding = isAuthoritarian && cause === "term";
  // Priorité : dictature (l'autoritarisme prime) > victoire (promesse tenue)
  // > cadrage neutre par cause.
  const framing = dictatorEnding
    ? dictatorFraming(worldState.turn)
    : victory
      ? victoryFraming(worldState.turn)
      : causeFraming(cause, worldState.turn);
  // Sert au style (palette autoritaire sombre / accent victoire) sans
  // toucher la vraie cause.
  const frameCause = dictatorEnding
    ? "dictature"
    : victory
      ? "victoire"
      : cause;

  // N4 — courbe d'approbation (jusqu'à 30 points lissés).
  const approvalSeries = (indicatorHistory.approval ?? []).map((e) => e.value);
  // L5b — verdict moral 4 axes + phrase de presse fictive (T60 cathartique).
  const moral = moralVerdict(worldState);
  const press = pressVerdict(worldState);
  // N4 — barres horizontales pour les 4 axes politiques (profil final).
  const axes: Array<{ key: keyof PoliticalProfile; label: string }> = [
    { key: "economic", label: "économique" },
    { key: "societal", label: "sociétal" },
    { key: "international", label: "international" },
    { key: "authority", label: "autorité" },
  ];

  const handleShare = () => {
    const dataUrl = renderShareImage({
      cause,
      pretitle: framing.pretitle,
      archetype: sig.archetype,
      closing: sig.closing,
      stats: {
        approval: aggregates.meanSatisfaction,
        debtRatio: debt,
        balance,
      },
    });
    if (!dataUrl) return;
    downloadDataUrl(
      dataUrl,
      `moi-president-${cause}-mois-${worldState.turn}.png`,
    );
  };

  return (
    <div className="mandate-end-backdrop" data-cause={frameCause}>
      <div
        className="mandate-end-card"
        role="dialog"
        aria-modal="true"
        aria-label="Fin de mandat"
        data-cause={frameCause}
      >
        <div className="mandate-end-pretitle">{framing.pretitle}</div>

        <h2 className="mandate-end-title">{framing.title}</h2>

        <div className="mandate-end-signature-block">
          <div className="mandate-end-signature-prefix">
            {framing.signaturePrefix}
          </div>
          <div className="mandate-end-courant">{sig.courant}</div>
          <div className="mandate-end-signature-archetype">{sig.label}</div>
          <div className="mandate-end-ligne">
            Ligne politique : {sig.archetype}
          </div>
        </div>

        <p className="mandate-end-closing">« {sig.closing} »</p>

        <div className="mandate-end-stats">
          <div className="end-stat">
            <span className="end-stat-label">Cote finale</span>
            <span className="end-stat-value">
              {Math.round(aggregates.meanSatisfaction)} %
            </span>
          </div>
          <div className="end-stat">
            <span className="end-stat-label">Dette / PIB</span>
            <span className="end-stat-value">{debt.toFixed(0)} %</span>
          </div>
          <div className="end-stat">
            <span className="end-stat-label">Solde final</span>
            <span className="end-stat-value">
              {balance >= 0 ? "+" : ""}
              {balance.toFixed(0)} Md€
            </span>
          </div>
        </div>

        <PledgeReckoning />

        {/* L5b — verdict moral 4 axes : démocratie / économie / social /
            souveraineté. Distinct du profil politique : ici on juge ce
            que le président *laisse*, pas ce qu'il *était*. */}
        <section className="mandate-end-moral">
          <div className="recap-title">Bilan moral</div>
          <ul className="moral-axes">
            {moral.map((a) => (
              <li key={a.label} className="moral-axis" data-tone={a.tone}>
                <span className="moral-axis-label">{a.label}</span>
                <span className="moral-axis-verdict">
                  {VERDICT_TEXT(a.tone)}
                </span>
                <span className="moral-axis-detail">{a.verdict}</span>
                <span className="moral-axis-driver">{a.driver}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* L5b — phrase de presse fictive : le titre que le pays lira
            demain matin. Outlet choisi par profil, headline composée
            depuis le bilan moral. */}
        <blockquote className="mandate-end-press">
          <div className="press-meta">
            <span className="press-outlet">{press.outlet}</span>
            <span className="press-sep">·</span>
            <span className="press-date">{press.date}</span>
          </div>
          <p className="press-headline">« {press.headline} »</p>
        </blockquote>

        {/* N4 — récap cinématique : courbe approbation + radar profil. */}
        {approvalSeries.length > 1 && (
          <section className="mandate-end-recap">
            <div className="recap-title">Trajectoire d'approbation</div>
            <ApprovalSpark values={approvalSeries} />
          </section>
        )}

        <section className="mandate-end-recap">
          <div className="recap-title">Profil politique final</div>
          <ol className="profile-bars">
            {axes.map((a) => {
              const v = profile[a.key];
              const pct = ((v + 1) / 2) * 100; // -1..1 → 0..100
              return (
                <li key={a.key} className="profile-bar-row">
                  <span className="profile-bar-label">{a.label}</span>
                  <div className="profile-bar-track" aria-hidden="true">
                    <div
                      className="profile-bar-fill"
                      style={{
                        left: `${Math.min(50, pct)}%`,
                        width: `${Math.abs(pct - 50)}%`,
                      }}
                      data-direction={v >= 0 ? "right" : "left"}
                    />
                    <div className="profile-bar-zero" aria-hidden="true" />
                  </div>
                  <span className="profile-bar-value">{v.toFixed(2)}</span>
                </li>
              );
            })}
          </ol>
        </section>

        <section className="feedback-cta">
          <p className="feedback-cta-hint">
            Vous faites partie des tout premiers joueurs. Le jeu est en beta —
            votre avis nous aide à l'améliorer.
          </p>
          <button
            type="button"
            className="feedback-cta-btn"
            onClick={openFeedback}
          >
            ◇ DONNER MON AVIS
          </button>
        </section>

        <div className="mandate-end-actions">
          <button
            type="button"
            className="mandate-end-share"
            onClick={handleShare}
            title="Téléchargez une image partageable de votre bilan"
          >
            ↓ TÉLÉCHARGER LE BILAN
          </button>
          {cause === "term" && (
            <button
              type="button"
              className="mandate-end-reelect"
              onClick={handleReelect}
              title="Nouveau mandat dans le même contexte historique"
            >
              ⟳ RÉÉLECTION
            </button>
          )}
          <button
            type="button"
            className="mandate-end-restart"
            onClick={handleRestart}
          >
            ━━ NOUVELLE PARTIE ━━
          </button>
        </div>

        {SUPPORT_URL && (
          <p className="mandate-end-support">
            Ce jeu est gratuit, sans publicité, fait par une seule personne sur
            son temps libre.{" "}
            <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
              Soutenir le projet ♥
            </a>
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Contrat de campagne — le jugement final de la promesse : tenue,
 * mitigée ou trahie. Le pays n'oublie pas ce qu'on lui a promis.
 */
function PledgeReckoning() {
  const pledges = useGame((s) => s.pledges);
  if (pledges.length === 0) return null;
  return (
    <section className="mandate-end-pledges">
      <div className="recap-title">La promesse</div>
      <ul className="pledge-reckoning-list">
        {pledges.map((p) => {
          const tone =
            p.progress >= 1.0
              ? "sauve"
              : p.progress > -1.0
                ? "abime"
                : "ravage";
          const verdict =
            p.progress >= 1.0
              ? "Tenue. Rare, et le pays le sait."
              : p.progress >= 0.0
                ? "À moitié tenue — l'intention y était, pas les résultats."
                : p.progress > -1.0
                  ? "Restée lettre morte. On vous le rappellera."
                  : "Trahie. C'est ce que l'Histoire retiendra d'abord.";
          return (
            <li key={p.id} className="pledge-reckoning" data-tone={tone}>
              <span className="pledge-reckoning-label">« {p.label} »</span>
              <span className="pledge-reckoning-verdict">{verdict}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
